import { useEffect, useState } from "react";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";
import {
  fetchAssetHolders,
  fetchEmployeeAssets,
  returnAllAssets,
} from "../model/AssetUpload";

export const MAX_REASON_LEN = 255;
export const MAX_NOTES_LEN = 500;
export const RETURN_CONDITIONS = ["Good", "Damaged", "Missing parts"];

const EmployeeAssetsVM = ({ active, onReturned }) => {
  const { handleToastOpen } = useToast();
  const [holders, setHolders] = useState([]);
  const [selectedHolder, setSelectedHolder] = useState(null);
  const [assets, setAssets] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loadingHolders, setLoadingHolders] = useState(false);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonTouched, setReasonTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [doneMessage, setDoneMessage] = useState(""); // shown after all assets of someone are returned
  const [conditions, setConditions] = useState({}); // { asset_id: { condition, notes } }

  // Employees who hold assets (for the search box)
  const loadHolders = async () => {
    try {
      setLoadingHolders(true);
      const response = await fetchAssetHolders();
      const list = response.data?.data || [];
      setHolders(list);
      // keep the chosen employee's count up to date
      setSelectedHolder((prev) =>
        prev
          ? list.find(
              (h) =>
                h.employee_name === prev.employee_name &&
                (h.employee_code || "") === (prev.employee_code || "")
            ) || prev
          : prev
      );
    } catch (error) {
      handleToastOpen(
        "error",
        error.response?.data?.message || "Failed to load employees"
      );
      setHolders([]);
    } finally {
      setLoadingHolders(false);
    }
  };

  // Assets of the chosen employee (all ticked by default)
  const loadAssets = async (holder) => {
    if (!holder) {
      setAssets([]);
      setSelectedIds([]);
      return [];
    }
    try {
      setLoadingAssets(true);
      const response = await fetchEmployeeAssets(
        holder.employee_name,
        holder.employee_code
      );
      const list = response.data?.data || [];
      setAssets(list);
      setSelectedIds(list.map((a) => a.asset_id));
      return list;
    } catch (error) {
      handleToastOpen(
        "error",
        error.response?.data?.message || "Failed to load employee assets"
      );
      setAssets([]);
      setSelectedIds([]);
      return null;
    } finally {
      setLoadingAssets(false);
    }
  };

  // Refresh every time the tab is opened (assets may have changed elsewhere)
  useEffect(() => {
    if (active) {
      loadHolders();
      if (selectedHolder) loadAssets(selectedHolder);
    }
  }, [active]);

  const handleHolderChange = (holder) => {
    setDoneMessage("");
    setSelectedHolder(holder);
    loadAssets(holder);
  };

  const toggleAsset = (assetId) => {
    setSelectedIds((prev) =>
      prev.includes(assetId) ? prev.filter((id) => id !== assetId) : [...prev, assetId]
    );
  };

  const toggleAll = () => {
    setSelectedIds((prev) =>
      prev.length === assets.length ? [] : assets.map((a) => a.asset_id)
    );
  };

  const openReturn = () => {
    if (selectedIds.length === 0) {
      handleToastOpen("error", "Please select at least one asset");
      return;
    }
    setReason("");
    setReasonTouched(false);
    setConditions({}); // condition must be chosen for each asset
    setReturnOpen(true);
  };

  const closeReturn = () => {
    if (!saving) setReturnOpen(false);
  };

  // Condition / notes of one asset in the popup
  const setAssetCondition = (assetId, condition) => {
    setConditions((prev) => ({ ...prev, [assetId]: { ...prev[assetId], condition } }));
  };
  const setAssetNotes = (assetId, notes) => {
    setConditions((prev) => ({ ...prev, [assetId]: { ...prev[assetId], notes } }));
  };
  // Quick button: mark every selected asset as Good (keeps notes)
  const markAllGood = () => {
    setConditions((prev) => {
      const next = { ...prev };
      selectedIds.forEach((id) => {
        next[id] = { ...next[id], condition: "Good" };
      });
      return next;
    });
  };

  const reasonValid = reason.trim().length > 0;
  const allConditionsSet = selectedIds.every((id) =>
    RETURN_CONDITIONS.includes(conditions[id]?.condition)
  );

  const confirmReturn = async () => {
    if (!reasonValid) {
      setReasonTouched(true);
      return;
    }
    if (!allConditionsSet) {
      handleToastOpen("error", "Please choose the condition for every asset");
      return;
    }
    try {
      setSaving(true);
      const response = await returnAllAssets({
        employee_name: selectedHolder.employee_name,
        employee_code: selectedHolder.employee_code || "",
        asset_ids: selectedIds,
        return_reason: reason.trim(),
        conditions: selectedIds.reduce((acc, id) => {
          acc[id] = {
            condition: conditions[id]?.condition,
            notes: (conditions[id]?.notes || "").trim(),
          };
          return acc;
        }, {}),
      });
      handleToastOpen(
        "success",
        response.data?.message || "Assets returned successfully"
      );
      setReturnOpen(false);
      const remaining = await loadAssets(selectedHolder); // assets still with the employee
      if (remaining && remaining.length === 0) {
        // everything returned: clear the box and show a confirmation
        setDoneMessage(`All assets of ${selectedHolder.employee_name} are returned.`);
        setSelectedHolder(null);
      }
      loadHolders(); // counts change; employee disappears if nothing is left
      if (onReturned) onReturned(); // refresh the main asset list
    } catch (error) {
      handleToastOpen(
        "error",
        error.response?.data?.message || "Failed to return assets"
      );
      if (error.response?.status === 409) {
        setReturnOpen(false);
        loadAssets(selectedHolder); // someone changed them: show the latest list
      }
    } finally {
      setSaving(false);
    }
  };

  const selectedAssets = assets.filter((a) => selectedIds.includes(a.asset_id));

  return {
    doneMessage,
    holders,
    selectedHolder,
    assets,
    selectedIds,
    selectedAssets,
    loadingHolders,
    loadingAssets,
    returnOpen,
    reason,
    setReason,
    reasonTouched,
    setReasonTouched,
    reasonValid,
    conditions,
    setAssetCondition,
    setAssetNotes,
    markAllGood,
    allConditionsSet,
    saving,
    handleHolderChange,
    toggleAsset,
    toggleAll,
    openReturn,
    closeReturn,
    confirmReturn,
  };
};

export default EmployeeAssetsVM;